<template>
  <div class="metrics-score-dist">
    <div class="metrics-score-dist__header">
      <h3 class="metrics-score-dist__title heading-s">
        {{ $t("metrics.scoreDistribution") }}
      </h3>
      <eg-b-dropdown
        v-if="hasData"
        ref="viewDropdown"
        class="metrics-score-dist__view-switcher no-print"
        :text="viewLabel"
        right
        no-caret
        variant="link"
      >
        <eg-b-dropdown-item @click="view = 'chart'">{{
          $t("metrics.viewChart")
        }}</eg-b-dropdown-item>
        <eg-b-dropdown-item @click="view = 'table'">{{
          $t("metrics.viewTable")
        }}</eg-b-dropdown-item>
      </eg-b-dropdown>
    </div>

    <infinity-loading v-if="isLoading && !hasData" />

    <template v-else-if="hasData">
      <!-- Chart view -->
      <div
        v-if="view === 'chart'"
        ref="chartWrap"
        class="metrics-score-dist__chart-wrap"
      >
        <!-- Custom tooltip -->
        <div
          v-show="tooltipVisible"
          class="metrics-score-dist__tooltip"
          :style="tooltipStyle"
        >
          <div class="metrics-score-dist__tooltip-title">
            <span
              class="metrics-score-dist__tooltip-dot"
              :style="{ background: tooltipData.color }"
            />
            <span>{{ tooltipData.score }}</span>
          </div>
          <div class="metrics-score-dist__tooltip-row">
            <span class="metrics-score-dist__tooltip-label">{{
              $t("metrics.students")
            }}</span>
            <span class="metrics-score-dist__tooltip-value">
              {{ tooltipData.count }} ({{ tooltipData.percentage }}%)
            </span>
          </div>
        </div>

        <div class="metrics-score-dist__donut-container">
          <chart-js-doughnut
            ref="doughnut"
            :chart-data="chartData"
            :chart-options="chartOptions"
            :width="200"
            :height="200"
            :plugins="chartPlugins"
          />
        </div>
        <div class="metrics-score-dist__legend">
          <div
            v-for="(row, index) in filteredRows"
            :key="index"
            class="metrics-score-dist__legend-item"
            @mouseenter="highlightSegment(index)"
            @mouseleave="clearHighlight"
          >
            <span
              class="metrics-score-dist__legend-dot"
              :style="{ background: colorMap[index] }"
            />
            <span class="metrics-score-dist__legend-label"
              >{{ row.score }} pts</span
            >
          </div>
        </div>
      </div>

      <!-- Table view -->
      <div v-else class="metrics-score-dist__table-wrap">
        <eg-pv-table :items="distribution.rows" :fields="tableFields">
          <template #cell(score)="{ item }"> {{ item.score }} pts</template>
          <template #cell(count)="{ item }">
            {{ item.count }} ({{ item.percentage }}%)
          </template>
        </eg-pv-table>
      </div>
    </template>

    <metrics-empty-state v-else-if="!isLoading" />
  </div>
</template>

<script>
import MetricsEmptyState from "./metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import ChartJsDoughnut from "@/components/global/chartjs/chart_js_doughnut.vue";
import EgPvTable from "@/components/global/eg/eg_pv_table/eg_pv_table.vue";
import { SCORE_COLOR_LIST } from "@/views/metrics/constants.js";
import i18n from "@/i18n/i18n";

export default {
  name: "MetricsScoreDistribution",

  components: {
    MetricsEmptyState,
    InfinityLoading,
    ChartJsDoughnut,
    EgPvTable,
  },

  props: {
    distribution: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      view: "chart",
      tooltipVisible: false,
      tooltipData: { score: "", count: 0, percentage: 0, color: "" },
      tooltipStyle: {},
    };
  },

  computed: {
    hasData() {
      return this.distribution && this.distribution.rows;
    },

    tableFields() {
      return [
        { key: "score", label: this.$t("metrics.scoreHeader") },
        { key: "count", label: this.$t("metrics.studentsCountHeader") },
      ];
    },

    viewLabel() {
      return this.view === "chart"
        ? this.$t("metrics.viewChart")
        : this.$t("metrics.viewTable");
    },

    filteredRows() {
      if (!this.hasData) return [];
      return this.distribution.rows.filter((row) => row.count > 0);
    },

    // Assign colors so the segment with the most students gets the first (most vivid) color
    colorMap() {
      if (!this.hasData) return [];
      const rows = this.filteredRows;
      const rankOf = new Array(rows.length);
      rows
        .map((row, i) => ({ i, count: row.count || 0 }))
        .sort((a, b) => b.count - a.count)
        .forEach(({ i }, rank) => {
          rankOf[i] = rank;
        });
      return rows.map(
        (_, i) => SCORE_COLOR_LIST[rankOf[i] % SCORE_COLOR_LIST.length]
      );
    },

    chartData() {
      if (!this.hasData) return { datasets: [] };
      const rows = this.filteredRows;
      return {
        labels: rows.map((r) => r.score),
        datasets: [
          {
            data: rows.map((r) => r.count),
            backgroundColor: this.colorMap,
            borderWidth: 0,
            hoverOffset: 6,
            hoverBackgroundColor: this.colorMap,
          },
        ],
      };
    },

    totalStudents() {
      if (!this.hasData) return 0;
      return this.distribution.rows.reduce(
        (sum, row) => sum + (row.count || 0),
        0
      );
    },

    chartOptions() {
      return {
        cutout: "70%",
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        layout: { padding: 10 },
        onHover: (event, activeElements) => {
          event.native.target.style.cursor =
            activeElements.length > 0 ? "pointer" : "default";
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            external: (context) => {
              const { chart, tooltip } = context;
              if (!tooltip.opacity) {
                this.tooltipVisible = false;
                return;
              }
              const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;
              if (dataIndex === undefined) return;

              const row = this.filteredRows[dataIndex];
              this.tooltipData = {
                score: row.score,
                count: row.count,
                percentage: row.percentage,
                color: this.colorMap[dataIndex],
              };

              const wrapEl = this.$refs.chartWrap;
              if (!wrapEl) return;
              const canvasRect = chart.canvas.getBoundingClientRect();
              const wrapRect = wrapEl.getBoundingClientRect();
              const x = canvasRect.left - wrapRect.left + tooltip.caretX;
              const y = canvasRect.top - wrapRect.top + tooltip.caretY;

              this.tooltipStyle = {
                left: `${x}px`,
                top: `${y}px`,
              };
              this.tooltipVisible = true;
            },
          },
          centerText: {
            totalStudents: this.totalStudents,
            studentsLabel:
              this.totalStudents === 1
                ? i18n.t("metrics.student")
                : i18n.t("metrics.students"),
          },
        },
      };
    },

    chartPlugins() {
      return [
        {
          id: "centerText",
          afterDraw(chart) {
            const { ctx, chartArea } = chart;
            if (!chartArea) return;
            const { top, bottom, left, right } = chartArea;
            const cx = (left + right) / 2;
            const cy = (top + bottom) / 2;
            const total =
              chart.options.plugins?.centerText?.totalStudents ?? "";

            ctx.save();
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            ctx.font = "bold 22px sans-serif";
            ctx.fillStyle = "#1a2330";
            ctx.fillText(total, cx, cy - 8);

            ctx.font = "12px sans-serif";
            ctx.fillStyle = "#566370";
            ctx.fillText(
              chart.options.plugins?.centerText?.studentsLabel ??
                i18n.t("metrics.students"),
              cx,
              cy + 12
            );

            ctx.restore();
          },
        },
      ];
    },
  },

  methods: {
    highlightSegment(index) {
      const chart = this.$refs.doughnut?._chart;
      if (!chart) return;
      chart.setActiveElements([{ datasetIndex: 0, index }]);
      chart.update();
      this._showTooltipAtSegment(index);
    },

    clearHighlight() {
      const chart = this.$refs.doughnut?._chart;
      if (!chart) return;
      chart.setActiveElements([]);
      chart.update();
      this.tooltipVisible = false;
    },

    _showTooltipAtSegment(index) {
      const chart = this.$refs.doughnut?._chart;
      const row = this.filteredRows[index];
      if (!chart || !row) return;

      this.tooltipData = {
        score: row.score,
        count: row.count,
        percentage: row.percentage,
        color: this.colorMap[index],
      };

      const arc = chart.getDatasetMeta(0)?.data?.[index];
      if (!arc) return;

      const midAngle = (arc.startAngle + arc.endAngle) / 2;
      const midRadius = (arc.outerRadius + arc.innerRadius) / 2;
      const x = arc.x + Math.cos(midAngle) * midRadius;
      const y = arc.y + Math.sin(midAngle) * midRadius;

      const wrapRect = this.$refs.chartWrap.getBoundingClientRect();
      const canvasRect = chart.canvas.getBoundingClientRect();

      this.tooltipStyle = {
        left: `${canvasRect.left - wrapRect.left + x}px`,
        top: `${canvasRect.top - wrapRect.top + y}px`,
      };
      this.tooltipVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "metrics_card";

.metrics-score-dist {
  @include metrics-card;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
  width: 400px;

  @media (max-width: 1200px) {
    width: 100%;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__view-switcher {
    @include metrics-dropdown;
  }

  &__title {
    font-size: var(--typography-font-size-fs-400);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-primary, #1a2330);
    margin: 0;
  }

  &__chart-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    height: 270px;
  }

  &__tooltip {
    position: absolute;
    z-index: 10;
    pointer-events: none;
    transform: translate(-50%, calc(-100% - 8px));
    min-width: 160px;
    padding: 10px 12px;
    border-radius: var(--radius-200, 4px);
    border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    background: var(--color-surface-neutral-background, #fff);
    box-shadow: 0 2px 6px 2px rgba(0, 0, 0, 0.06);
  }

  &__tooltip-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--typography-font-size-fs-300, 13px);
    font-weight: var(--typography-font-weight-fw-600, 600);
    color: var(--color-text-neutral-primary, #1a2330);
    margin-bottom: 6px;
  }

  &__tooltip-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__tooltip-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-size: var(--typography-font-size-fs-200, 12px);
  }

  &__tooltip-label {
    color: var(--color-text-neutral-secondary, #566370);
  }

  &__tooltip-value {
    font-weight: var(--typography-font-weight-fw-600, 600);
    color: var(--color-text-neutral-primary, #1a2330);
  }

  &__donut-container {
    width: 200px;
    height: 200px;
    margin: 0 auto;
    flex-shrink: 0;
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    justify-content: center;
  }

  &__legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }

  &__legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__legend-label {
    font-size: var(--typography-font-size-fs-200, 12px);
    color: var(--color-text-neutral-primary, #1a2330);
  }

  &__table-wrap {
    overflow-y: auto;
    height: 270px;
  }
}
</style>
